import React, { useState, useEffect, useMemo } from 'react';
import {
  VStack,
  HStack,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Progress,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Select,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  useColorMode,
  useColorModeValue,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  Textarea
} from '@chakra-ui/react';
import {
  FiDownload,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiTarget,
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiPieChart,
  FiDollarSign,
  FiUsers,
  FiSettings,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiMoreVertical,
  FiSun,
  FiMoon,
  FiCheckCircle,
  FiAlertTriangle,
  FiAlertCircle,
  FiInfo,
  FiFileText,
  FiShare2,
  FiPrinter
} from 'react-icons/fi';

const ReportsAndAnalytics = ({
  projectTasks = [],
  categories = [],
  budgetSummary = {},
  projectId,
  currentProject
}) => {
  // Dark theme support
  const { colorMode, toggleColorMode } = useColorMode();

  // All color mode values at component level
  const bgPrimary = useColorModeValue('white', 'gray.800');
  const bgSecondary = useColorModeValue('gray.50', 'gray.700');
  const bgTertiary = useColorModeValue('gray.100', 'gray.600');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const shadowColor = useColorModeValue('rgba(0,0,0,0.1)', 'rgba(0,0,0,0.3)');

  // Premium gradient backgrounds
  const gradientBg = useColorModeValue(
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
  );

  const cardGradient = useColorModeValue(
    'linear-gradient(145deg, #ffffff 0%, #f7fafc 100%)',
    'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)'
  );

  // State Management
  const [selectedReportType, setSelectedReportType] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('progress');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(true);
  const [selectedView, setSelectedView] = useState('detailed');
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Modal controls
  const {
    isOpen: isExportModalOpen,
    onOpen: onExportModalOpen,
    onClose: onExportModalClose
  } = useDisclosure();

  const toast = useToast();

  // Get project currency
  const projectCurrency = budgetSummary?.currency || currentProject?.currency || 'AED';

  // Enhanced Utility Functions
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const formatCurrency = (amount, currency = projectCurrency) => {
    const projectCurr = currency || projectCurrency || 'AED';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: projectCurr,
      minimumFractionDigits: 1,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCompactCurrency = (amount, currency = projectCurrency) => {
    const projectCurr = currency || projectCurrency || 'AED';

    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: projectCurr,
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
        // notation: 'compact',
        // compactDisplay: 'short'
      }).format(amount);
    } else if (amount >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: projectCurr,
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
        // notation: 'compact',
        // compactDisplay: 'short'
      }).format(amount);
    }
    return formatCurrency(amount, projectCurr);
  };

  const getPeriodName = (periodStr) => {
    const [year, month] = periodStr.split('-');
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -12; i <= 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  // Enhanced Task Metrics Calculation
  const calculateTaskMetrics = () => {
    if (!projectTasks || projectTasks.length === 0) return null;

    const metrics = {
      total: projectTasks.length,
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      overBudget: 0,
      onTrack: 0,
      underBudget: 0,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 0,
      totalBudgetedCost: 0,
      totalConsumedCost: 0,
      totalBudgetedHours: 0,
      totalConsumedHours: 0,
      totalQuantity: 0,
      totalInstalledQuantity: 0,
      startedBudgetedHours: 0,
      startedConsumedHours: 0,
      startedInstalledQuantity: 0,
      startedTotalQuantity: 0,
      totalEarnedManhours: 0,
      costVariance: 0,
      lapsedManhours: 0,
      costConstant: 0,
      totalEarnedCost: 0,

    };

    projectTasks.forEach(task => {
      // Quantity-based progress calculation
      const quantityProgress = task.quantity > 0
        ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
        : 0;
      // console.log('Task:', task, 'Quantity Progress:', quantityProgress);
      // Progress status
      if (quantityProgress >= 100) metrics.completed++;
      else if (quantityProgress > 0) metrics.inProgress++;
      else metrics.notStarted++;

      // Cost calculations
      const budgetedCost = task.budgetedCost || 0;
      const consumedCost = task.consumedCost || 0;

      metrics.totalBudgetedCost += budgetedCost;
      metrics.totalConsumedCost += consumedCost;
      metrics.totalBudgetedHours += (task.totalBudgetedManhours || 0);
      metrics.totalConsumedHours += (task.totalConsumedManhours || 0);
      metrics.totalQuantity += (task.quantity || 0);
      metrics.totalInstalledQuantity += (task.totalInstalledQuantity || 0);
      metrics.totalEarnedManhours += (task.totalInstalledQuantity || 0) * ((task.productivity || 0));
      metrics.costVariance += (budgetedCost - consumedCost);


      if (task.totalConsumedManhours > 0 && (task.totalInstalledQuantity || 0) > 0) {
        metrics.startedBudgetedHours += (task.totalBudgetedManhours || 0);
        metrics.startedConsumedHours += (task.totalConsumedManhours || 0);
        metrics.startedInstalledQuantity += (task.totalInstalledQuantity || 0);
        metrics.startedTotalQuantity += (task.quantity || 0);
      }

      // CPI calculation
      const actualManhours = task.totalConsumedManhours || 0;
      const earnedManhours = (task.totalInstalledQuantity || 0) * (task.productivity || 0);
      const cpi = actualManhours > 0 ? earnedManhours / actualManhours : null;

      // Budget status
      if (cpi === null) metrics.onTrack++;
      else if (cpi >= 1.1) metrics.underBudget++;
      else if (cpi >= 0.9) metrics.onTrack++;
      else metrics.overBudget++;

      // Risk assessment
      if (cpi === null) metrics.lowRisk++;
      else if (cpi < 0.8) metrics.highRisk++;
      else if (cpi < 0.9) metrics.mediumRisk++;
      else metrics.lowRisk++;
    });

    // Calculate overall percentages
    metrics.completionPercentage = metrics.total > 0 ? (metrics.completed / metrics.total) * 100 : 0;

    metrics.progressPercentage = metrics.totalQuantity > 0 ? (metrics.totalEarnedManhours / metrics.totalBudgetedHours) : 0;
    metrics.lapsedManhours = metrics.totalEarnedManhours - metrics.totalConsumedHours;
    metrics.costConstant = metrics.totalConsumedHours > 0 ? (metrics.totalConsumedCost / metrics.totalConsumedHours) : 0;
    metrics.costVariance = metrics.lapsedManhours * metrics.costConstant;
    metrics.totalEarnedCost = metrics.totalEarnedManhours * metrics.costConstant;
    // console.log(metrics.costConstant);
    metrics.budgetEfficiency = metrics.totalConsumedCost > 0 ? (metrics.totalBudgetedCost / metrics.totalConsumedCost) * 100 : 100;

    // metrics.overallCPI = metrics.totalConsumedHours > 0 ? 
    //   (metrics.totalBudgetedHours * (metrics.progressPercentage / 100)) / metrics.totalConsumedHours : 1;
    const startedEv = metrics.totalConsumedHours > 0 ? (metrics.startedInstalledQuantity / metrics.startedTotalQuantity) * metrics.startedBudgetedHours : 0;


    metrics.overallCPI = metrics.startedConsumedHours > 0 ? (metrics.totalEarnedManhours / metrics.totalConsumedHours) : 1;

    return metrics;
  };

  // Enhanced Category-wise Report
  const getCategoryWiseReport = () => {
    const categoryReport = {};

    categories.forEach(category => {
      const categoryTasks = projectTasks.filter(task => task.categoryName === category.name);

      if (categoryTasks.length === 0) {
        categoryReport[category.name] = {
          taskCount: 0,
          totalBudgetedHours: 0,
          totalConsumedHours: 0,
          totalEarnedManhours: 0,
          startedBudgetedHours: 0,
          startedConsumedHours: 0,
          startedInstalledQuantity: 0,
          startedTotalQuantity: 0,
          lapsedManhours: 0,
          costVariance: 0,
          totalBudgetedCost: 0,
          totalConsumedCost: 0,
          totalQuantity: 0,
          totalInstalledQuantity: 0,
          quantityProgress: 0,
          cpi: 1,
          costVariance: 0,
          status: 'No Data',
          riskLevel: 'Low',
          efficiency: 0
        };
        return;
      }

      const totals = categoryTasks.reduce((acc, task) => {
        if (task.totalConsumedManhours > 0 && (task.totalInstalledQuantity || 0) > 0) {
          acc.startedBudgetedHours += (task.totalBudgetedManhours || 0);
          acc.startedConsumedHours += (task.totalConsumedManhours || 0);
          acc.startedInstalledQuantity += (task.totalInstalledQuantity || 0);
          acc.startedTotalQuantity += (task.quantity || 0);
        }
        acc.budgetedHours += (task.totalBudgetedManhours || 0);
        acc.consumedHours += (task.totalConsumedManhours || 0);
        acc.budgetedCost += (task.budgetedCost || 0);
        acc.consumedCost += (task.consumedCost || 0);
        acc.quantity += (task.quantity || 0);
        acc.installedQuantity += (task.totalInstalledQuantity || 0);
        acc.totalEarnedManhours += (task.totalInstalledQuantity || 0) * ((task.productivity || 0));
        return acc;
      }, {
        budgetedHours: 0,
        consumedHours: 0,
        budgetedCost: 0,
        consumedCost: 0,
        totalEarnedManhours: 0,
        lapsedManhours: 0,
        costVariance: 0,
        quantity: 0,
        installedQuantity: 0,
        startedBudgetedHours: 0,
        startedConsumedHours: 0,
        startedInstalledQuantity: 0,
        startedTotalQuantity: 0,
      });

      const quantityProgress = totals.quantity > 0 ? (totals.installedQuantity / totals.quantity) * 100 : 0;
      const lapsedManhours = totals.totalEarnedManhours - totals.consumedHours;

      const costConstant = totals.consumedHours > 0 ? (totals.budgetedCost / totals.budgetedHours) : 0;
      const costVariance = lapsedManhours * costConstant;
      const totalEarnedCost = totals.totalEarnedManhours * costConstant;
      const categoryCPI = totals.consumedHours > 0
        ? totals.totalEarnedManhours / totals.consumedHours
        : null;

      let status = 'On Track';
      let riskLevel = 'Low';

      if (categoryCPI !== null && categoryCPI < 0.8) {
        status = 'Critical';
        riskLevel = 'High';
      } else if (categoryCPI !== null && categoryCPI < 0.9) {
        status = 'Over Budget';
        riskLevel = 'Medium';
      } else if (categoryCPI !== null && categoryCPI >= 1.1) {
        status = 'Under Budget';
        riskLevel = 'Low';
      }

      categoryReport[category.name] = {
        taskCount: categoryTasks.length,
        totalBudgetedHours: totals.budgetedHours,
        totalConsumedHours: totals.consumedHours,
        totalBudgetedCost: totals.budgetedCost,
        totalConsumedCost: totals.consumedCost,
        totalQuantity: totals.quantity,
        totalInstalledQuantity: totals.installedQuantity,
        totalEarnedManhours: totals.totalEarnedManhours,
        totalEarnedCost: totalEarnedCost,
        lapsedManhours,

        quantityProgress,
        cpi: categoryCPI,
        costVariance,
        status,
        riskLevel,
        efficiency: quantityProgress
      };
    });

    return categoryReport;
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = projectTasks.filter(task => {
      const matchesSearch = !searchQuery ||
        task.masterSubTask?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.categoryName?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || task.categoryName === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    // Sort tasks
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'progress':
          aValue = a.quantity > 0 ? ((a.totalInstalledQuantity || 0) / a.quantity) * 100 : 0;
          bValue = b.quantity > 0 ? ((b.totalInstalledQuantity || 0) / b.quantity) * 100 : 0;
          break;
        case 'cost':
          aValue = a.consumedCost || 0;
          bValue = b.consumedCost || 0;
          break;
        case 'cpi':
          const aCPI = calculateTaskCPI(a);
          const bCPI = calculateTaskCPI(b);
          aValue = aCPI || 0;
          bValue = bCPI || 0;
          break;
        case 'name':
          aValue = a.masterSubTask?.name || '';
          bValue = b.masterSubTask?.name || '';
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [projectTasks, searchQuery, selectedCategory, sortBy, sortOrder]);

  // Calculate task CPI
  const calculateTaskCPI = (task) => {
    const plannedManhours = task.totalBudgetedManhours || 0;
    const actualManhours = task.totalConsumedManhours || 0;
    const quantityProgress = task.quantity > 0 ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100 : 0;
    const earnedManhours = task.totalInstalledQuantity * task.productivity;

    if (actualManhours === 0) return null;
    if (plannedManhours === 0) return null;

    const earnedValue = earnedManhours;
    return earnedValue / actualManhours;
  };

  // Enhanced Export Functionality
  const handleExportReport = async (exportType = 'comprehensive') => {
    setLoading(true);
    try {
      const taskMetrics = calculateTaskMetrics();
      const categoryReport = getCategoryWiseReport();

      const reportData = {
        projectInfo: {
          name: currentProject?.name || 'Unnamed Project',
          id: projectId,
          currency: projectCurrency,
          generatedAt: new Date().toISOString(),
          period: getPeriodName(selectedPeriod),
          reportType: exportType
        },
        summary: {
          ...budgetSummary,
          ...taskMetrics
        },
        categoryReport,
        tasks: filteredAndSortedTasks
      };

      const csvContent = generateEnhancedCSV(reportData, exportType);

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentProject?.name || 'Project'}_${exportType}_Report_${projectCurrency}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: `${exportType} report exported successfully`,
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error',
        description: 'Failed to export report',
        status: 'error',
        duration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced CSV Generation
  const generateEnhancedCSV = (data, exportType) => {
    let csv = `PROJECT REPORTS & ANALYTICS\n`;
    csv += `Project: ${data.projectInfo.name}\n`;
    csv += `Currency: ${data.projectInfo.currency}\n`;
    csv += `Generated: ${new Date(data.projectInfo.generatedAt).toLocaleString()}\n`;
    csv += `Period: ${data.projectInfo.period}\n`;
    csv += `Report Type: ${exportType}\n`;
    csv += `Theme: ${colorMode} mode\n\n`;

    // Executive Summary
    csv += `EXECUTIVE SUMMARY\n`;
    csv += `Total Tasks,${data.summary.total || 0}\n`;
    csv += `Completed Tasks,${data.summary.completed || 0}\n`;
    csv += `Overall Progress,${data.summary.progressPercentage?.toFixed(1) || 0}\n`;
    csv += `Project CPI,${data.summary.overallCPI?.toFixed(2) || 'N/A'}\n`;
    csv += `Total Budget (${data.projectInfo.currency}),${data.summary.totalBudgetedCost?.toFixed(2) || 0}\n`;
    csv += `Total Consumed (${data.projectInfo.currency}),${data.summary.totalConsumedCost?.toFixed(2) || 0}\n`;
    csv += `Budget Man-hours (MH),${data.summary.totalBudgetedHours?.toFixed(2) || 0}\n`;
    csv += `Earned Man-hours (MH),${data.summary.totalEarnedManhours?.toFixed(2) || 0}\n`;
    csv += `Consumed Man-hours (MH),${data.summary.totalConsumedHours?.toFixed(2) || 0}\n`;
    csv += `Budget Efficiency,${data.summary.budgetEfficiency?.toFixed(1) || 0}%\n\n`;

    // Category Performance
    csv += `CATEGORY PERFORMANCE\n`;
    csv += `Category,Tasks,Budget Man-hours (MH),Budget Cost (${data.projectInfo.currency}),Earned Man-hours (MH),Earned Cost (${data.projectInfo.currency}),Consumed Man-hours (MH),Consumed Cost (${data.projectInfo.currency}),Cost Variance (${data.projectInfo.currency}),Progress %,CPI,Status,Risk Level\n`;

    Object.entries(data.categoryReport).forEach(([categoryName, categoryData]) => {
      csv += `"${categoryName}",`;
      csv += `${categoryData.taskCount},`;
      csv += `${categoryData.totalBudgetedHours.toFixed(2)},`;
      csv += `${categoryData.totalBudgetedCost.toFixed(2)},`;
      csv += `${categoryData.totalEarnedManhours.toFixed(2)},`;
      csv += `${(categoryData.totalEarnedCost || 0).toFixed(2)},`;
      csv += `${categoryData.totalConsumedHours.toFixed(2)},`;
      csv += `${categoryData.totalConsumedCost.toFixed(2)},`;
      csv += `${categoryData.costVariance.toFixed(2)},`;
      csv += `${categoryData.quantityProgress.toFixed(1)}%,`;
      csv += `${Number.isFinite(categoryData.cpi) ? categoryData.cpi.toFixed(2) : 'N/A'},`;
      csv += `"${categoryData.status}",`;
      csv += `"${categoryData.riskLevel}"\n`;
    });

    if (exportType === 'comprehensive' || exportType === 'detailed') {
      csv += `\nDETAILED TASK ANALYSIS\n`;
      csv += `Task,Category,Total Quantity,Installed Quantity,Unit,Progress %,Budget Cost (${data.projectInfo.currency}),Consumed Cost (${data.projectInfo.currency}),Budget Hours,Earned Hours,Consumed Hours,CPI,Status\n`;

      data.tasks.forEach(task => {
        const quantityProgress = task.quantity > 0 ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100 : 0;
        const status = quantityProgress >= 100 ? 'Complete' : quantityProgress > 0 ? 'In Progress' : 'Not Started';
        const cpi = calculateTaskCPI(task);

        csv += `"${task.masterSubTask?.name || 'Unknown'}",`;
        csv += `"${task.categoryName || 'Unknown'}",`;
        csv += `${task.quantity || 0},`;
        csv += `${task.totalInstalledQuantity || 0},`;
        csv += `"${task.unit || 'No'}",`;
        csv += `${quantityProgress.toFixed(1)}%,`;
        csv += `${task.budgetedCost || 0},`;
        csv += `${task.consumedCost || 0},`;
        csv += `${task.totalBudgetedManhours || 0},`;
        csv += `${((task.totalInstalledQuantity || 0) * (task.productivity || 0)).toFixed(2)},`;
        csv += `${task.totalConsumedManhours || 0},`;
        csv += `${cpi?.toFixed(2) || 'N/A'},`;
        csv += `"${status}"\n`;
      });
    }

    return csv;
  };

  // Color utility functions
  const getProgressColor = (progress) => {
    if (progress >= 90) return 'green';
    if (progress >= 70) return 'blue';
    if (progress >= 50) return 'yellow';
    if (progress >= 25) return 'orange';
    return 'red';
  };

  const getCPIColor = (cpi) => {
    if (cpi >= 1.1) return 'green';
    if (cpi >= 0.9) return 'yellow';
    return 'red';
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'complete': case 'under budget': return 'green';
      case 'in progress': case 'on track': return 'blue';
      case 'over budget': case 'critical': return 'red';
      case 'not started': return 'gray';
      default: return 'gray';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'red';
      default: return 'gray';
    }
  };

  // Calculate metrics
  const taskMetrics = calculateTaskMetrics();
  const categoryReport = getCategoryWiseReport();

  if (!projectTasks || projectTasks.length === 0) {
    return (
      <Alert status="info" bg={bgSecondary} borderColor={borderColor}>
        <AlertIcon />
        <VStack align="start" spacing={2}>
          <Text color={textPrimary}>No data available for reports</Text>
          <Text fontSize="sm" color={textMuted}>Configure task quantities and record progress to generate comprehensive reports.</Text>
        </VStack>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch" w="100%">
      {/* Enhanced Header */}
      <Card bg={cardGradient} borderColor={borderColor} borderWidth="2px" shadow={`0 8px 32px ${shadowColor}`}>
        <CardHeader>
          <HStack justify="space-between" align="center" wrap="wrap" spacing={4}>
            <VStack align="start" spacing={1}>
              <HStack>
                <Heading size="lg" color="blue.500">
                  <HStack>
                    <FiBarChart2 />
                    <Text>Reports & Analytics</Text>
                  </HStack>
                </Heading>
                <Badge colorScheme="green" variant="solid" fontSize="sm" px={3}>
                  {projectCurrency}
                </Badge>
                <IconButton
                  icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
                  onClick={toggleColorMode}
                  variant="ghost"
                  size="sm"
                  aria-label="Toggle theme"
                  color={textSecondary}
                />
              </HStack>
              <Text color={textMuted} fontSize="sm">
                Comprehensive project performance analysis and insights in {projectCurrency}
              </Text>
            </VStack>

            <HStack spacing={3} wrap="wrap">
              <FormControl maxW="200px">
                <FormLabel color={textSecondary} fontSize="sm" mb={1}>Report Period</FormLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  bg={bgSecondary}
                  borderColor={borderColor}
                  size="sm"
                >
                  {generatePeriodOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Menu>
                <MenuButton
                  as={Button}
                  leftIcon={<FiDownload />}
                  colorScheme="blue"
                  size="sm"
                  isLoading={loading}
                >
                  Export
                </MenuButton>
                <MenuList bg={bgPrimary} borderColor={borderColor}>
                  <MenuItem onClick={() => handleExportReport('overview')}>
                    <FiFileText style={{ marginRight: '8px' }} />
                    Executive Summary
                  </MenuItem>
                  <MenuItem onClick={() => handleExportReport('detailed')}>
                    <FiBarChart2 style={{ marginRight: '8px' }} />
                    Detailed Analysis
                  </MenuItem>
                  <MenuItem onClick={() => handleExportReport('comprehensive')}>
                    <FiTarget style={{ marginRight: '8px' }} />
                    Comprehensive Report
                  </MenuItem>
                </MenuList>
              </Menu>

              <Button
                leftIcon={<FiRefreshCw />}
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </HStack>
          </HStack>
        </CardHeader>

        <CardBody>
          {/* Control Panel */}
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} mb={6}>
            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Report Type</FormLabel>
              <Select
                value={selectedReportType}
                onChange={(e) => setSelectedReportType(e.target.value)}
                bg={bgSecondary}
                borderColor={borderColor}
                size="sm"
              >
                <option value="overview">Project Overview</option>
                <option value="detailed">Detailed Analysis</option>
                <option value="category">Category Performance</option>
                <option value="cost">Cost Analysis</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Category Filter</FormLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                bg={bgSecondary}
                borderColor={borderColor}
                size="sm"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Search Tasks</FormLabel>
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={bgSecondary}
                borderColor={borderColor}
                size="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Options</FormLabel>
              <VStack spacing={2} align="start">
                <HStack>
                  <Switch
                    isChecked={showAdvancedMetrics}
                    onChange={(e) => setShowAdvancedMetrics(e.target.checked)}
                    colorScheme="blue"
                    size="sm"
                  />
                  <Text fontSize="sm" color={textSecondary}>Advanced Metrics</Text>
                </HStack>
              </VStack>
            </FormControl>
          </Grid>
        </CardBody>
      </Card>

      {/* Enhanced Key Metrics Dashboard */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4, xl: 5 }} spacing={4}>
        <Stat bg={bgPrimary} p={4} rounded="lg" borderColor={borderColor} border="1px">
          <StatLabel color={textMuted} fontSize="xs">Overall Progress</StatLabel>
          <StatNumber color="blue.500" fontSize="2xl">
            {taskMetrics?.progressPercentage?.toFixed(1) || 0}
          </StatNumber>
          <StatHelpText color={textMuted} fontSize="xs">
            {formatNumber(taskMetrics?.totalEarnedManhours || 0)} / {formatNumber(taskMetrics?.totalBudgetedHours || 0)} MH
          </StatHelpText>
        </Stat>

        <Stat bg={bgPrimary} p={4} rounded="lg" borderColor={borderColor} border="1px">
          <StatLabel color={textMuted} fontSize="xs">Task Completion</StatLabel>
          <StatNumber color="green.500" fontSize="2xl">
            {taskMetrics?.completed || 0}/{taskMetrics?.total || 0}
          </StatNumber>
          <StatHelpText color={textMuted} fontSize="xs">
            {((taskMetrics?.completed || 0) / (taskMetrics?.total || 1) * 100).toFixed(0)}% complete
          </StatHelpText>
        </Stat>

        <Stat bg={bgPrimary} p={4} rounded="lg" borderColor={borderColor} border="1px">
          <StatLabel color={textMuted} fontSize="xs">Project CPI</StatLabel>
          <StatNumber
            color={getCPIColor(taskMetrics?.overallCPI || 1)}
            fontSize="2xl"
          >
            {taskMetrics?.overallCPI?.toFixed(2) <= 1 ? taskMetrics?.overallCPI?.toFixed(2) : '1.00' || 'N/A'}
          </StatNumber>
          <StatHelpText color={textMuted} fontSize="xs">
            {taskMetrics?.overallCPI >= 1 ? 'Under budget' : 'Over budget'}
          </StatHelpText>
        </Stat>

        <Stat bg={bgPrimary} p={4} rounded="lg" borderColor={borderColor} border="1px">
          <StatLabel color={textMuted} fontSize="xs">Budget Performance</StatLabel>
          <StatNumber color="purple.500" fontSize="xl">
            {formatCompactCurrency(taskMetrics?.totalConsumedCost || 0)}
          </StatNumber>
          <StatHelpText color={textMuted} fontSize="xs">
            of {formatCompactCurrency(taskMetrics?.totalBudgetedCost || 0)}
          </StatHelpText>
        </Stat>

        <Stat bg={bgPrimary} p={4} rounded="lg" borderColor={borderColor} border="1px">
          <StatLabel color={textMuted} fontSize="xs">Risk Assessment</StatLabel>
          <HStack spacing={2}>
            <Badge colorScheme="red" size="sm">{taskMetrics?.highRisk || 0}</Badge>
            <Badge colorScheme="yellow" size="sm">{taskMetrics?.mediumRisk || 0}</Badge>
            <Badge colorScheme="green" size="sm">{taskMetrics?.lowRisk || 0}</Badge>
          </HStack>
          <StatHelpText color={textMuted} fontSize="xs">
            High / Med / Low risk tasks
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Report Content Tabs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList bg={bgSecondary} borderColor={borderColor}>
          <Tab color={textSecondary} _selected={{ color: 'blue.500', bg: bgPrimary }}>
            <FiActivity style={{ marginRight: '8px' }} />
            Overview
          </Tab>
          <Tab color={textSecondary} _selected={{ color: 'blue.500', bg: bgPrimary }}>
            <FiPieChart style={{ marginRight: '8px' }} />
            Category Analysis
          </Tab>
          <Tab color={textSecondary} _selected={{ color: 'blue.500', bg: bgPrimary }}>
            <FiDollarSign style={{ marginRight: '8px' }} />
            Cost Analysis
          </Tab>
          <Tab color={textSecondary} _selected={{ color: 'blue.500', bg: bgPrimary }}>
            <FiBarChart2 style={{ marginRight: '8px' }} />
            Detailed Tasks
          </Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel p={0} pt={6}>
            <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
              {/* Progress Distribution */}
              <Card bg={bgPrimary} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md" color={textPrimary}>Task Status Distribution</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4}>
                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={3} h={3} bg="green.500" rounded="full" />
                        <Text color={textPrimary}>Completed</Text>
                      </HStack>
                      <Text fontWeight="bold" color="green.500">{taskMetrics?.completed || 0}</Text>
                    </HStack>
                    <Progress
                      value={((taskMetrics?.completed || 0) / (taskMetrics?.total || 1)) * 100}
                      colorScheme="green"
                      size="sm"
                      w="100%"
                    />

                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={3} h={3} bg="blue.500" rounded="full" />
                        <Text color={textPrimary}>In Progress</Text>
                      </HStack>
                      <Text fontWeight="bold" color="blue.500">{taskMetrics?.inProgress || 0}</Text>
                    </HStack>
                    <Progress
                      value={((taskMetrics?.inProgress || 0) / (taskMetrics?.total || 1)) * 100}
                      colorScheme="blue"
                      size="sm"
                      w="100%"
                    />

                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={3} h={3} bg="gray.500" rounded="full" />
                        <Text color={textPrimary}>Not Started</Text>
                      </HStack>
                      <Text fontWeight="bold" color="gray.500">{taskMetrics?.notStarted || 0}</Text>
                    </HStack>
                    <Progress
                      value={((taskMetrics?.notStarted || 0) / (taskMetrics?.total || 1)) * 100}
                      colorScheme="gray"
                      size="sm"
                      w="100%"
                    />
                  </VStack>
                </CardBody>
              </Card>

              {/* Budget Performance */}
              <Card bg={bgPrimary} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md" color={textPrimary}>Budget Performance</Heading>
                </CardHeader>
                <CardBody style={{ textAlign: 'right' }}>
                  <VStack spacing={4}>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Under Budget</Text>
                      <Badge colorScheme="green">{taskMetrics?.underBudget || 0} tasks</Badge>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>On Track</Text>
                      <Badge colorScheme="yellow">{taskMetrics?.onTrack || 0} tasks</Badge>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Over Budget</Text>
                      <Badge colorScheme="red">{taskMetrics?.overBudget || 0} tasks</Badge>
                    </HStack>

                    <Divider />

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Budget:</Text>
                      <Text fontWeight="bold" color={textPrimary}>
                        {formatNumber(taskMetrics?.totalBudgetedHours || 0)} MH  <br />

                        {formatCompactCurrency(taskMetrics?.totalBudgetedCost || 0)}
                      </Text>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Consumed:</Text>
                      <Text fontWeight="bold" color={textPrimary}>
                        {formatNumber(taskMetrics?.totalConsumedHours || 0)} MH  <br />

                        {formatCompactCurrency(taskMetrics?.totalConsumedCost || 0)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Earned MH:</Text>
                      <Text fontWeight="bold" color={textPrimary}>
                        {formatNumber(taskMetrics?.totalEarnedManhours || 0)} MH  <br />

                        {/* {formatCompactCurrency((taskMetrics?.productivity) || 0)}  */}
                      </Text>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>MH Variance:</Text>
                      <Text
                        fontWeight="bold"
                        color={(taskMetrics?.lapsedManhours) >= 0 ? 'green.500' : 'red.500'}
                      >
                        {(taskMetrics?.lapsedManhours || 0) >= 0 ? '+' : ''}
                        {formatNumber(taskMetrics?.lapsedManhours || 0)} MH
                      </Text>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Cost variance:</Text>
                      <Text
                        fontWeight="bold"
                        color={(taskMetrics?.costVariance) >= 0 ? 'green.500' : 'red.500'}
                      >
                        {formatCompactCurrency((taskMetrics?.costVariance || 0))}
                      </Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </Grid>
          </TabPanel>

          {/* Category Analysis Tab */}
          <TabPanel p={0} pt={6}>
            <Card bg={bgPrimary} borderColor={borderColor}>
              <CardHeader>
                <Heading size="md" color={textPrimary}>Category Performance Analysis</Heading>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg={bgSecondary}>
                      <Tr>
                        <Th color={textSecondary}>Category</Th>
                        <Th isNumeric color={textSecondary}>Tasks</Th>
                        <Th isNumeric color={textSecondary}>Progress </Th>
                        <Th isNumeric color={textSecondary}>CPI</Th>
                        <Th isNumeric color={textSecondary}>Budget </Th>
                        <Th isNumeric color={textSecondary}>Earned </Th>

                        <Th isNumeric color={textSecondary}>Consumed </Th>

                        <Th isNumeric color={textSecondary}>Variance </Th>
                        <Th textAlign="center" color={textSecondary}>Status</Th>
                        <Th textAlign="center" color={textSecondary}>Risk</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(categoryReport).map(([categoryName, data]) => (
                        // console.log('datasss',data);
                        <Tr key={categoryName} _hover={{ bg: hoverBg }}>
                          <Td>
                            <Text fontWeight="medium" color={textPrimary}>{categoryName}</Text>
                          </Td>
                          <Td isNumeric>
                            <Text color={textPrimary}>{data.taskCount}</Text>
                          </Td>
                          <Td isNumeric>
                            <HStack justify="flex-end" spacing={2}>
                              <Progress
                                value={data.quantityProgress}
                                colorScheme={getProgressColor(data.quantityProgress)}
                                size="sm"
                                w="60px"
                              />
                              {/* <Text fontSize="sm" fontWeight="bold" color={textPrimary}>
                                {data.quantityProgress.toFixed(1)}
                              </Text> */}
                            </HStack>
                          </Td>
                          <Td isNumeric>
                            <Badge
                              colorScheme={Number.isFinite(data.cpi) ? getCPIColor(data.cpi) : 'gray'}
                              fontSize="xs"
                            >
                              {Number.isFinite(data.cpi) ? data.cpi.toFixed(2) : 'N/A'}
                            </Badge>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm" color={textPrimary}>
                              {formatNumber(data.totalBudgetedHours)} MH <br />
                              {formatCompactCurrency(data.totalBudgetedCost)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm" color={textPrimary}>
                              {formatNumber(data.totalEarnedManhours)} MH <br />

                              {formatCompactCurrency(data.totalEarnedCost)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm" color={textPrimary}>
                              {formatNumber(data.totalConsumedHours)} MH <br />

                              {formatCompactCurrency(data.totalConsumedCost)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <HStack justify="flex-end" spacing={1}>
                              {data.costVariance >= 0 ?
                                <FiTrendingUp color="green" size={12} /> :
                                <FiTrendingDown color="red" size={12} />
                              }
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={data.costVariance >= 0 ? 'green.500' : 'red.500'}
                              >
                                {formatCompactCurrency(Math.abs(data.costVariance))}
                              </Text>
                            </HStack>
                          </Td>
                          <Td textAlign="center">
                            <Badge colorScheme={getStatusColor(data.status)} size="sm">
                              {data.status}
                            </Badge>
                          </Td>
                          <Td textAlign="center">
                            <Badge colorScheme={getRiskColor(data.riskLevel)} size="sm">
                              {data.riskLevel}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Cost Analysis Tab */}
          <TabPanel p={0} pt={6}>
            <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
              <Card bg={bgPrimary} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md" color={textPrimary}>
                    <HStack>
                      <FiDollarSign />
                      <Text>Cost Breakdown</Text>
                    </HStack>
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4}>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Budgeted:</Text>
                      <Text fontWeight="bold" color="blue.500">
                        {formatCurrency(taskMetrics?.totalBudgetedCost || 0)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Earned:</Text>
                      <Text fontWeight="bold" color="blue.500">
                        {formatCurrency(taskMetrics?.totalEarnedCost || 0)}
                      </Text>
                    </HStack>

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Total Consumed:</Text>
                      <Text
                        fontWeight="bold"
                        color={
                          (taskMetrics?.totalConsumedCost || 0) > (taskMetrics?.totalEarnedCost || 0)
                            ? "red.500"
                            : "purple.500"
                        }
                      >
                        {formatCurrency(taskMetrics?.totalConsumedCost || 0)}
                      </Text>
                    </HStack>


                    <Divider />

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Cost Variance:</Text>
                      <Text
                        fontWeight="bold"
                        color={(taskMetrics?.costVariance) >= 0 ? 'green.500' : 'red.500'}
                      >
                        {(taskMetrics?.costVariance) >= 0 ? '+' : ''}
                        {formatCurrency((taskMetrics?.costVariance || 0))}
                      </Text>
                    </HStack>

                    {/* <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Budget Utilization:</Text>
                      <Text fontWeight="bold" color={textPrimary}>
                        {taskMetrics?.totalBudgetedCost > 0 ?
                          ((taskMetrics.totalConsumedCost / taskMetrics.totalBudgetedCost) * 100).toFixed(1) : 0
                        }%
                      </Text>
                    </HStack> */}
                  </VStack>
                </CardBody>
              </Card>

              <Card bg={bgPrimary} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md" color={textPrimary}>
                    <HStack>
                      <FiClock />
                      <Text>Manhours Analysis</Text>
                    </HStack>
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4}>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Budgeted Hours:</Text>
                      <Text fontWeight="bold" color="blue.500">
                        {formatNumber(taskMetrics?.totalBudgetedHours || 0)} hrs
                      </Text>
                    </HStack>
                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Earned Hours:</Text>
                      <Text fontWeight="bold" color="purple.500">
                        {formatNumber(taskMetrics?.totalEarnedManhours || 0)} hrs
                      </Text>
                    </HStack>


                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Consumed Hours:</Text>
                      <Text fontWeight="bold" color="purple.500">
                        {formatNumber(taskMetrics?.totalConsumedHours || 0)} hrs
                      </Text>
                    </HStack>

                    <Divider />

                    <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Hour Variance:</Text>
                      <Text
                        fontWeight="bold"
                        color={(taskMetrics?.lapsedManhours) >= 0 ? 'green.500' : 'red.500'}
                      >
                        {(taskMetrics?.lapsedManhours) >= 0 ? '+' : ''}
                        {formatNumber((taskMetrics?.lapsedManhours || 0))} hrs
                      </Text>
                    </HStack>

                    {/* <HStack justify="space-between" w="100%">
                      <Text color={textMuted}>Hour Utilization:</Text>
                      <Text fontWeight="bold" color={textPrimary}>
                        {taskMetrics?.totalBudgetedHours > 0 ?
                          ((taskMetrics.totalConsumedHours / taskMetrics.totalBudgetedHours) * 100).toFixed(1) : 0
                        }%
                      </Text>
                    </HStack> */}
                  </VStack>
                </CardBody>
              </Card>
            </Grid>
          </TabPanel>

          {/* Detailed Tasks Tab */}
          <TabPanel p={0} pt={6}>
            <Card bg={bgPrimary} borderColor={borderColor}>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md" color={textPrimary}>Detailed Task Analysis</Heading>
                  <HStack spacing={2}>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      size="sm"
                      maxW="150px"
                      bg={bgSecondary}
                    >
                      <option value="progress">Progress</option>
                      <option value="cost">Cost</option>

                      <option value="name">Name</option>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </Button>
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg={bgSecondary}>
                      <Tr>
                        <Th minW="200px" color={textSecondary}>Task Description</Th>
                        <Th color={textSecondary}>Category</Th>
                        {/* <Th isNumeric color={textSecondary}>Progress </Th> */}
                        <Th isNumeric color={textSecondary}>CPI</Th>
                        <Th isNumeric color={textSecondary}>Budget </Th>
                        <Th isNumeric color={textSecondary}>Consumed </Th>
                        <Th isNumeric color={textSecondary}>Hours Budget</Th>
                        <Th isNumeric color={textSecondary}>Hours Earned</Th>

                        <Th isNumeric color={textSecondary}>Consumed Manhour</Th>
                        <Th textAlign="center" color={textSecondary}>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredAndSortedTasks.map(task => {
                        const quantityProgress = task.quantity > 0 ?
                          ((task.totalInstalledQuantity || 0) / task.quantity) * 100 : 0;
                        const cpi = calculateTaskCPI(task);
                        const status = quantityProgress >= 100 ? 'Complete' :
                          quantityProgress > 0 ? 'In Progress' : 'Not Started';

                        return (
                          <Tr key={task.id} _hover={{ bg: hoverBg }}>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color={textPrimary} noOfLines={2}>
                                  {task.masterSubTask?.name}
                                </Text>
                                <Text fontSize="xs" color={textMuted} fontFamily="mono">
                                  {task.masterSubTask?.masterActivity?.code}
                                </Text>
                              </VStack>
                            </Td>
                            <Td>
                              <Badge size="sm" colorScheme="blue" variant="subtle">
                                {task.categoryName}
                              </Badge>
                            </Td>
                            {/* <Td isNumeric>
                              <VStack spacing={1}>
                                <Progress
                                  value={quantityProgress}
                                  colorScheme={getProgressColor(quantityProgress)}
                                  size="sm"
                                  w="60px"
                                />
                                <Text fontSize="xs" fontWeight="bold" color={textPrimary}>
                                  {quantityProgress.toFixed(1)}
                                </Text>
                              </VStack>
                            </Td> */}
                            <Td isNumeric>
                              <Badge
                                colorScheme={cpi ? getCPIColor(cpi) : 'gray'}
                                fontSize="xs"
                              >
                                {cpi?.toFixed(2) || 'N/A'}
                              </Badge>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color={textPrimary}>
                                {formatCompactCurrency(task.budgetedCost || 0)}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color={textPrimary}>
                                {formatCompactCurrency(task.consumedCost || 0)}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color={textPrimary}>

                                {formatNumber(task.totalBudgetedManhours || 0)} MH
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color={textPrimary}>

                                {formatNumber((task.totalInstalledQuantity * task.productivity) || 0)} MH
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color={textPrimary}>
                                {formatNumber(task.totalConsumedManhours || 0)} MH
                              </Text>
                            </Td>
                            <Td textAlign="center">
                              <Badge colorScheme={getStatusColor(status)} size="sm">
                                {status}
                              </Badge>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default ReportsAndAnalytics;
